/**
 * User menu component
 * Dropdown menu for user actions
 */

import { Dropdown, Avatar, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { useSiteAuth } from '@/stores/useSiteAuthStore';

const { Text } = Typography;

interface UserMenuProps {
  showName?: boolean;
}

export function UserMenu({ showName = true }: UserMenuProps) {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout: jwtLogout } = useAuthStore();
  const { authEnabled: siteAuthEnabled, logout: siteLogout } = useSiteAuth();

  const handleMenuClick: MenuProps['onClick'] = async ({ key }) => {
    switch (key) {
      case 'settings':
        navigate('/settings');
        break;
      case 'help':
        window.open('https://github.com/your-repo/docs', '_blank');
        break;
      case 'logout':
        // Handle both JWT auth and site auth
        if (isAuthenticated) {
          jwtLogout();
        }
        if (siteAuthEnabled) {
          await siteLogout();
        }
        navigate('/');
        break;
    }
  };

  // Build menu items - include logout only if site auth is enabled or user is JWT authenticated
  const showLogout = siteAuthEnabled || isAuthenticated;

  const menuItems: MenuProps['items'] = [
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
    {
      key: 'help',
      icon: <QuestionCircleOutlined />,
      label: 'Help & Documentation',
    },
    ...(showLogout
      ? [
          {
            type: 'divider' as const,
          },
          {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Sign Out',
            danger: true,
          },
        ]
      : []),
  ];

  // Always show menu for site auth users, even without JWT auth
  const showMenu = isAuthenticated || siteAuthEnabled;

  if (!showMenu) {
    return (
      <Avatar
        size="small"
        icon={<UserOutlined />}
        style={{ backgroundColor: 'var(--color-primary)' }}
      />
    );
  }

  return (
    <Dropdown
      menu={{ items: menuItems, onClick: handleMenuClick }}
      trigger={['click']}
      placement="bottomRight"
    >
      <Space
        style={{
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: 6,
          transition: 'background 0.2s',
        }}
        className="user-menu-trigger"
      >
        <Avatar
          size="small"
          src={user?.avatar}
          icon={!user?.avatar && <UserOutlined />}
          style={{ backgroundColor: 'var(--color-primary)' }}
        />
        {showName && user?.name && (
          <Text style={{ maxWidth: 120 }} ellipsis>
            {user.name}
          </Text>
        )}
      </Space>
    </Dropdown>
  );
}

export default UserMenu;
